import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import moment from "moment";
import "moment/locale/es";
import React, { useCallback, useEffect, useState } from "react";
import Calendar from "react-calendar";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import { useAnalyticsCore } from "../../../cores/analytics";
import "react-calendar/dist/Calendar.css";
import "./TikTokAnalyticsDateRangeFilter.scss";

const formatRangeLabel = (since: string, until: string) => {
  const fmt = (iso: string) => {
    const m = moment(iso).locale("es");
    const month = m.format("MMM").replace(/\./g, "").toLowerCase();
    return `${m.date()} ${month}. ${m.year()}`;
  };
  return `${fmt(since)} - ${fmt(until)}`;
};

const SHORT_WEEKDAY_ES_ISO = ["L", "M", "X", "J", "V", "S", "D"];

const formatShortWeekdayEs = (_locale: string | undefined, date: Date) => {
  const d = date.getDay();
  const iso = d === 0 ? 6 : d - 1;
  return SHORT_WEEKDAY_ES_ISO[iso];
};

type CalendarRange = [Date, Date];

const clampMaxToday = (d: Date) => {
  const t = new Date();
  t.setHours(23, 59, 59, 999);
  return d > t ? t : d;
};

export const TikTokAnalyticsDateRangeFilter = () => {
  const { date, updateDate } = useAnalyticsCore();
  const [open, setOpen] = useState(false);
  const [leftView, setLeftView] = useState(() =>
    moment(date.since).startOf("month").toDate()
  );
  const [range, setRange] = useState<CalendarRange>(() => [
    moment(date.since).toDate(),
    moment(date.until).toDate(),
  ]);

  useEffect(() => {
    if (!open) {
      setRange([moment(date.since).toDate(), moment(date.until).toDate()]);
      setLeftView(moment(date.since).startOf("month").toDate());
    }
  }, [date.since, date.until, open]);

  const commitRange = useCallback(
    (start: Date, end: Date) => {
      const s = moment(clampMaxToday(start)).format("YYYY-MM-DD");
      const u = moment(clampMaxToday(end)).format("YYYY-MM-DD");
      const [since, until] = moment(s).isAfter(moment(u)) ? [u, s] : [s, u];
      updateDate({ mode: "Custom", since, until });
    },
    [updateDate]
  );

  const onCalendarChange = (value: Date | Date[] | null) => {
    if (!value || !Array.isArray(value)) return;
    const d0 = value[0];
    const d1 = value[1];
    if (!d0) return;
    const end = d1 ?? d0;
    setRange([d0, end]);
    if (d0 && d1) commitRange(d0, end);
  };

  const applyPreset = (since: Date, until: Date) => {
    const s = clampMaxToday(since);
    const u = clampMaxToday(until);
    setRange([s, u]);
    setLeftView(moment(s).startOf("month").toDate());
    commitRange(s, u);
  };

  const presets: { label: string; getRange: () => [Date, Date] }[] = [
    {
      label: "Ayer",
      getRange: () => {
        const d = moment().subtract(1, "day").toDate();
        return [d, d];
      },
    },
    {
      label: "Última semana",
      getRange: () => [
        moment().subtract(7, "days").toDate(),
        moment().subtract(1, "day").toDate(),
      ],
    },
    {
      label: "Mes actual",
      getRange: () => [
        moment().startOf("month").toDate(),
        moment().toDate(),
      ],
    },
    {
      label: "Últimos 30 días",
      getRange: () => [
        moment().subtract(30, "days").toDate(),
        moment().toDate(),
      ],
    },
    {
      label: "Mes pasado",
      getRange: () => [
        moment().subtract(1, "month").startOf("month").toDate(),
        moment().subtract(1, "month").endOf("month").toDate(),
      ],
    },
    {
      label: "Últimos 90 días",
      getRange: () => [
        moment().subtract(90, "days").toDate(),
        moment().toDate(),
      ],
    },
  ];

  const maxDate = new Date();
  const rangeLabel = formatRangeLabel(date.since, date.until);

  return (
    <Dropdown isOpen={open} toggle={() => setOpen((v) => !v)} direction="down">
      <DropdownToggle
        tag="button"
        type="button"
        caret={false}
        aria-expanded={open}
        aria-haspopup
        className={cn("tiktok-analytics-page-header__date-pill")}
      >
        <FontAwesomeIcon icon={["fas", "calendar-alt"]} className="me-2" fixedWidth />
        <span className="tiktok-analytics-page-header__date-pill-label">
          {rangeLabel}
        </span>
      </DropdownToggle>
      <DropdownMenu end className="tiktok-analytics-date-range__menu border-0 p-0">
        <div className="tiktok-analytics-date-range__panel">
          <div className="tiktok-analytics-date-range__presets">
            {presets.map((p) => (
              <button
                key={p.label}
                type="button"
                className="tiktok-analytics-date-range__preset"
                onClick={() => {
                  const [s, u] = p.getRange();
                  applyPreset(s, u);
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="tiktok-analytics-date-range__calendars">
            <Calendar
              className="tiktok-analytics-date-range__calendar tiktok-analytics-date-range__calendar--double"
              selectRange
              showDoubleView
              value={range}
              onChange={onCalendarChange}
              activeStartDate={leftView}
              onActiveStartDateChange={(args) => {
                const next = args.activeStartDate;
                if (next) setLeftView(next);
              }}
              maxDate={maxDate}
              locale="es-ES"
              calendarType="ISO 8601"
              formatShortWeekday={formatShortWeekdayEs}
              showNeighboringMonth={false}
            />
          </div>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};
